import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FiUser, FiMail, FiPhone, FiBriefcase, FiLock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import * as authService from '../../services/auth';
import '../../styles/main.scss';

const Profile = () => {
  const { user, updateUser } = useAuth();

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    department: '',
    position: '',
    phone: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        department: user.department || '',
        position: user.position || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const updateProfileMutation = useMutation(
    (payload) => authService.updateProfile(payload),
    {
      onSuccess: (data) => {
        const updatedUser = data?.data || data?.user || data;
        if (updatedUser) {
          updateUser(updatedUser);
        }
        toast.success(data?.message || 'Profile updated successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update profile');
      },
    }
  );

  const changePasswordMutation = useMutation(
    ({ currentPassword, newPassword }) =>
      authService.changePassword(currentPassword, newPassword),
    {
      onSuccess: (data) => {
        toast.success(data?.message || 'Password updated successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: '',
        });
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update password');
      },
    }
  );

  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();

    if (!profileData.name) {
      toast.error('Name is required');
      return;
    }

    updateProfileMutation.mutate({
      name: profileData.name,
      department: profileData.department || undefined,
      position: profileData.position || undefined,
      phone: profileData.phone || undefined,
    });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      toast.error('Current and new password are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  return (
    <div className="profile-page">
      <div className="page-header">
        <div>
          <h1>My Profile</h1>
          <p>Manage your personal information and account security</p>
        </div>
      </div>

      <div className="page-content">
        <div className="profile-layout">
          <motion.div
            className="profile-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2>Profile Information</h2>
            <p>Update your basic information used across the system</p>

            <form onSubmit={handleProfileSubmit} className="profile-form">
              <div className="form-group">
                <label>Full Name</label>
                <div className="input-with-icon">
                  <FiUser className="input-icon" />
                  <input
                    type="text"
                    className="form-control"
                    value={profileData.name}
                    onChange={(e) =>
                      handleProfileChange('name', e.target.value)
                    }
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    className="form-control"
                    value={profileData.email}
                    disabled
                  />
                </div>
                <small className="text-muted">
                  Email is managed by the administrator
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Department</label>
                  <div className="input-with-icon">
                    <FiBriefcase className="input-icon" />
                    <input
                      type="text"
                      className="form-control"
                      value={profileData.department}
                      onChange={(e) =>
                        handleProfileChange('department', e.target.value)
                      }
                      placeholder="e.g. Finance, HR"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    className="form-control"
                    value={profileData.position}
                    onChange={(e) =>
                      handleProfileChange('position', e.target.value)
                    }
                    placeholder="Your job title"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <div className="input-with-icon">
                  <FiPhone className="input-icon" />
                  <input
                    type="tel"
                    className="form-control"
                    value={profileData.phone}
                    onChange={(e) =>
                      handleProfileChange('phone', e.target.value)
                    }
                    placeholder="Contact number"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateProfileMutation.isLoading}
                >
                  {updateProfileMutation.isLoading
                    ? 'Saving...'
                    : 'Save Changes'}
                </button>
              </div>
            </form>
          </motion.div>

          <motion.div
            className="profile-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2>Change Password</h2>
            <p>Keep your account secure by using a strong password</p>

            <form onSubmit={handlePasswordSubmit} className="profile-form">
              <div className="form-group">
                <label>Current Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon" />
                  <input
                    type="password"
                    className="form-control"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      handlePasswordChange('currentPassword', e.target.value)
                    }
                    placeholder="Enter your current password"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      handlePasswordChange('newPassword', e.target.value)
                    }
                    placeholder="Choose a strong password"
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={passwordData.confirmNewPassword}
                    onChange={(e) =>
                      handlePasswordChange(
                        'confirmNewPassword',
                        e.target.value
                      )
                    }
                    placeholder="Re-enter new password"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-secondary"
                  disabled={changePasswordMutation.isLoading}
                >
                  {changePasswordMutation.isLoading
                    ? 'Updating...'
                    : 'Update Password'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
